/**
 * product-new-pdp-gallery.js
 * Vanilla custom element for the new PDP gallery.
 * Hooks into Dawn's pub/sub variant-change event (PUB_SUB_EVENTS.variantChange).
 * Syncs multiple placements (primary + secondary) on the same page.
 */

class ProductPdpGallery extends HTMLElement {
  /** @type {Map<string, Set<ProductPdpGallery>>} keyed by product ID */
  static registry = new Map();

  constructor() {
    super();
    this._activeTabIndex = 0;
    this._activeVariantId = null;
    this._galleryData = null;
    this._unsubVariant = null;
    this._slider = null;
    this._onScroll = null;
    this._fadeTimer = null;
  }

  connectedCallback() {
    this._activeVariantId = this.dataset.initialVariantId;
    this._parseData();
    if (this._galleryData) {
      const summary = Object.fromEntries(
        Object.entries(this._galleryData).map(([vid, vdata]) => [
          vid,
          (vdata.tabs || []).map((t) => ({
            label: t.label,
            items: t.items.length,
            _debug_tab_media_size: t._debug_tab_media_size,
            _debug_use_variant_images: t._debug_use_variant_images,
          })),
        ])
      );
      console.debug('[pdp-gallery] gallery summary (variantId → tabs → itemCount):', summary);
    }
    this._registerSelf();
    this._subscribeVariant();
    this._render(false);
  }

  disconnectedCallback() {
    this._unregisterSelf();
    if (this._unsubVariant) {
      this._unsubVariant();
      this._unsubVariant = null;
    }
    if (this._fadeTimer) {
      clearTimeout(this._fadeTimer);
      this._fadeTimer = null;
    }
    this._teardownScrollListener();
  }

  // ─────────────────────────────────────────────
  // Setup
  // ─────────────────────────────────────────────

  _parseData() {
    const script = this.querySelector('script.pdp-gallery__json');
    if (!script) return;
    try {
      this._galleryData = JSON.parse(script.textContent);
    } catch (e) {
      console.warn('[pdp-gallery] could not parse gallery JSON', e);
    }
  }

  _registerSelf() {
    const pid = this.dataset.productId;
    if (!ProductPdpGallery.registry.has(pid)) {
      ProductPdpGallery.registry.set(pid, new Set());
    }
    ProductPdpGallery.registry.get(pid).add(this);
  }

  _unregisterSelf() {
    const pid = this.dataset.productId;
    ProductPdpGallery.registry.get(pid)?.delete(this);
  }

  _subscribeVariant() {
    if (typeof subscribe !== 'function') {
      console.warn('[pdp-gallery] Dawn subscribe() not available — variant switching will not work');
      return;
    }
    const sectionId = this.dataset.sectionId;
    console.debug('[pdp-gallery] subscribed to variant-change, sectionId:', sectionId);
    this._unsubVariant = subscribe('variant-change', ({ data }) => {
      if (!data) return;
      if (data.sectionId !== sectionId) {
        console.debug('[pdp-gallery] variant-change ignored (sectionId mismatch:', data.sectionId, 'vs', sectionId, ')');
        return;
      }
      const variant = data.variant;
      if (!variant) return;
      const newId = String(variant.id);
      console.debug('[pdp-gallery] variant-change → switching to', newId, '(was', this._activeVariantId, ')');
      if (newId === this._activeVariantId) return;
      this._activeVariantId = newId;
      this._activeTabIndex = 0;
      this._render(true);
    });
  }

  // ─────────────────────────────────────────────
  // Rendering
  // ─────────────────────────────────────────────

  _render(animate) {
    if (!this._galleryData) return;

    const variantData =
      this._galleryData[this._activeVariantId] ||
      this._galleryData[Object.keys(this._galleryData)[0]];
    if (!variantData || !Array.isArray(variantData.tabs) || variantData.tabs.length === 0) {
      console.debug('[pdp-gallery] _render: no tabs for variant', this._activeVariantId);
      return;
    }

    const tabs = variantData.tabs;
    const tabIndex = Math.min(this._activeTabIndex, tabs.length - 1);
    const activeTab = tabs[tabIndex];

    // Tab bar shown when multiple tabs OR the single tab has a label
    const showTabbar = tabs.length > 1 || (tabs.length === 1 && tabs[0].label !== null);

    let inner = this.querySelector('.pdp-gallery__inner');
    if (!inner) {
      inner = document.createElement('div');
      inner.className = 'pdp-gallery__inner';
      this.appendChild(inner);
    }

    const applyContent = () => {
      this._teardownScrollListener();
      inner.innerHTML =
        this._buildDesktopHTML(tabs, tabIndex, activeTab, showTabbar) +
        this._buildMobileHTML(tabs, tabIndex, activeTab, showTabbar);
      this._bindTabButtons(inner, tabs);
      this._setupScrollDots(inner, activeTab.items.length);
      this._bindArrows(inner);
    };

    if (animate && inner.children.length > 0) {
      if (this._fadeTimer) clearTimeout(this._fadeTimer);
      inner.style.transition = 'opacity 200ms ease';
      inner.style.opacity = '0';
      this._fadeTimer = setTimeout(() => {
        this._fadeTimer = null;
        applyContent();
        void inner.offsetHeight;
        inner.style.opacity = '1';
      }, 210);
    } else {
      inner.style.transition = '';
      inner.style.opacity = '1';
      applyContent();
    }
  }

  // ── Desktop ────────────────────────────────

  _buildDesktopHTML(tabs, tabIndex, activeTab, showTabbar) {
    const items = activeTab.items || [];
    let grid = '';

    if (items.length === 1) {
      // Single item: span both columns, keep portrait ratio
      grid += this._buildItemHTML(items[0], 0, false, true);
    } else {
      // First 2 items → visual row 1
      for (let i = 0; i < Math.min(2, items.length); i++) {
        grid += this._buildItemHTML(items[i], i, false);
      }
    }

    // Tab bar row — spans both columns, between row 1 and row 2
    if (showTabbar) {
      grid += `<div class="pdp-gallery__tabbar-row">${this._buildTabbarHTML(tabs, tabIndex, 'desktop')}</div>`;
    }

    // Items after row 1:
    // - Videos are always full-width (spans both cols) regardless of position
    // - Last item of an odd-count batch is also full-width
    const remaining = items.slice(2);
    for (let j = 0; j < remaining.length; j++) {
      const item = remaining[j];
      const isLast = j === remaining.length - 1;
      const fullWidth = item.type === 'video' || (isLast && remaining.length % 2 === 1);
      grid += this._buildItemHTML(item, j + 2, fullWidth);
    }

    return `<div class="pdp-gallery__desktop"><div class="pdp-gallery__grid">${grid}</div></div>`;
  }

  _buildItemHTML(item, index, fullWidth = false, solo = false) {
    const cls = fullWidth
      ? 'pdp-gallery__item pdp-gallery__item--full--itg'
      : solo
      ? 'pdp-gallery__item pdp-gallery__item--solo'
      : 'pdp-gallery__item';
    return `<div class="${cls}" data-index="${index}">${this._buildMediaHTML(item, index, fullWidth || solo)}</div>`;
  }

  // ── Mobile ─────────────────────────────────

  _buildMobileHTML(tabs, tabIndex, activeTab, showTabbar) {
    const items = activeTab.items || [];
    const isSecondary = this.dataset.placement === 'secondary';
    const tabbarHTML = showTabbar ? this._buildTabbarHTML(tabs, tabIndex, 'mobile') : '';

    if (isSecondary) {
      // Secondary placement: stacked 2-column grid — no slider, no arrows, no dots.
      // Matches the reference where below-fold content is a static grid.
      const cells = items
        .map((item, i) => {
          const isLast = i === items.length - 1;
          const fullWidth = item.type === 'video' || (isLast && items.length % 2 === 1);
          const cls = fullWidth
            ? 'pdp-gallery__item pdp-gallery__item--full--itg'
            : 'pdp-gallery__item';
          return `<div class="${cls}" data-index="${i}">${this._buildMediaHTML(item, i, fullWidth)}</div>`;
        })
        .join('');

      return `<div class="pdp-gallery__mobile pdp-gallery__mobile--stacked">${tabbarHTML}<div class="pdp-gallery__stack">${cells}</div></div>`;
    }

    // Primary placement: horizontal scroll-snap slider with prev/next arrows + dots
    const slides = items
      .map(
        (item, i) =>
          `<div class="pdp-gallery__slide" data-index="${i}">${this._buildMediaHTML(item, i, false)}</div>`
      )
      .join('');

    const dots = items
      .map(
        (_, i) =>
          `<span class="pdp-gallery__dot${i === 0 ? ' pdp-gallery__dot--active' : ''}" data-dot="${i}" aria-label="Image ${i + 1}"></span>`
      )
      .join('');

    const chevL = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="18" height="18" aria-hidden="true"><polyline points="15 18 9 12 15 6"></polyline></svg>`;
    const chevR = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="18" height="18" aria-hidden="true"><polyline points="9 18 15 12 9 6"></polyline></svg>`;

    return `<div class="pdp-gallery__mobile"><div class="pdp-gallery__slider-wrapper" data-count="${items.length}"><button class="pdp-gallery__arrow pdp-gallery__arrow--prev" type="button" aria-label="Previous image">${chevL}</button><div class="pdp-gallery__slider" role="region" aria-label="Product images">${slides}</div><button class="pdp-gallery__arrow pdp-gallery__arrow--next" type="button" aria-label="Next image">${chevR}</button>${tabbarHTML}</div><div class="pdp-gallery__dots" aria-label="Slide indicators">${dots}</div></div>`;
  }

  // ── Shared helpers ──────────────────────────

  _buildTabbarHTML(tabs, activeIndex, context) {
    const buttons = tabs
      .map((tab, i) => {
        const isActive = i === activeIndex;
        const label = tab.label != null ? tab.label : `View ${i + 1}`;
        return `<button class="pdp-gallery__tab${isActive ? ' pdp-gallery__tab--active' : ''}" role="tab" aria-selected="${isActive}" tabindex="${isActive ? 0 : -1}" data-tab-index="${i}" data-tab-context="${context}">${this._esc(label)}</button>`;
      })
      .join('');

    return `<div class="pdp-gallery__tabbar" role="tablist" aria-label="Gallery views">${buttons}</div>`;
  }

  _buildMediaHTML(item, index, fullWidth = false) {
    if (item.type === 'image') {
      const sizes = fullWidth
        ? '(min-width: 990px) 60vw, 100vw'
        : '(min-width: 990px) 30vw, (min-width: 750px) 50vw, 100vw';
      return `<img src="${this._escAttr(item.src)}" srcset="${this._escAttr(item.srcset || '')}" sizes="${sizes}" alt="${this._escAttr(item.alt || '')}" loading="${index < 4 ? 'eager' : 'lazy'}" width="600" height="800">`;
    }
    if (item.type === 'video') {
      return `<video src="${this._escAttr(item.src)}" poster="${this._escAttr(item.poster || '')}" autoplay muted loop playsinline></video>`;
    }
    return '';
  }

  // ─────────────────────────────────────────────
  // Event binding
  // ─────────────────────────────────────────────

  _bindTabButtons(container, tabs) {
    container.querySelectorAll('.pdp-gallery__tab').forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = parseInt(btn.dataset.tabIndex, 10);
        this._switchTab(idx);
        const pid = this.dataset.productId;
        ProductPdpGallery.registry.get(pid)?.forEach((inst) => {
          if (inst !== this) inst._switchTabQuiet(idx);
        });
      });

      btn.addEventListener('keydown', (e) => {
        const ctx = btn.dataset.tabContext;
        const allInContext = [
          ...container.querySelectorAll(`.pdp-gallery__tab[data-tab-context="${ctx}"]`),
        ];
        const cur = allInContext.indexOf(btn);

        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          const next = (cur + 1) % allInContext.length;
          allInContext[next].click();
          allInContext[next].focus();
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          const prev = (cur - 1 + allInContext.length) % allInContext.length;
          allInContext[prev].click();
          allInContext[prev].focus();
        } else if (e.key === 'Home') {
          e.preventDefault();
          allInContext[0].click();
          allInContext[0].focus();
        } else if (e.key === 'End') {
          e.preventDefault();
          allInContext[allInContext.length - 1].click();
          allInContext[allInContext.length - 1].focus();
        }
      });
    });
  }

  _setupScrollDots(container, count) {
    const slider = container.querySelector('.pdp-gallery__slider');
    if (!slider) return;
    this._slider = slider;

    this._onScroll = () => {
      const w = slider.offsetWidth;
      if (!w) return;
      const idx = Math.round(slider.scrollLeft / w);
      container.querySelectorAll('.pdp-gallery__dot').forEach((dot, i) => {
        dot.classList.toggle('pdp-gallery__dot--active', i === idx);
      });
    };

    slider.addEventListener('scroll', this._onScroll, { passive: true });
  }

  _bindArrows(container) {
    const slider = container.querySelector('.pdp-gallery__slider');
    if (!slider) return;

    container.querySelector('.pdp-gallery__arrow--prev')?.addEventListener('click', () => {
      slider.scrollBy({ left: -slider.offsetWidth, behavior: 'smooth' });
    });

    container.querySelector('.pdp-gallery__arrow--next')?.addEventListener('click', () => {
      slider.scrollBy({ left: slider.offsetWidth, behavior: 'smooth' });
    });
  }

  _teardownScrollListener() {
    if (this._slider && this._onScroll) {
      this._slider.removeEventListener('scroll', this._onScroll);
    }
    this._slider = null;
    this._onScroll = null;
  }

  // ─────────────────────────────────────────────
  // Tab switching
  // ─────────────────────────────────────────────

  _switchTab(index) {
    this._activeTabIndex = index;
    this._render(true);
  }

  /** Switch tab without re-triggering sibling sync */
  _switchTabQuiet(index) {
    this._activeTabIndex = index;
    this._render(true);
  }

  // ─────────────────────────────────────────────
  // Utilities
  // ─────────────────────────────────────────────

  _esc(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  _escAttr(str) {
    return String(str).replace(/"/g, '&quot;');
  }
}

customElements.define('product-pdp-gallery', ProductPdpGallery);
